/**
 * CompanionSiteConfig parsing and normalization (doc 116 §2.6).
 */

import {
  DEFAULT_COMPANION_PROACTIVE_CAPS,
  DEFAULT_COMPANION_TRANSCRIPT_RETENTION,
  type CompanionAnnotationManifestPack,
  type CompanionAsrConfig,
  type CompanionProactiveCaps,
  type CompanionProactiveRules,
  type CompanionSiteConfig,
  type CompanionSiteKey,
  type CompanionTranscriptRetention
} from './types';

export interface CompanionConfigParseResult {
  ok: boolean;
  config?: CompanionSiteConfig;
  errors: string[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readString(record: Record<string, unknown>, key: string, required = true): string | null {
  const value = record[key];
  if (typeof value === 'string' && value.trim()) {
    return value.trim();
  }
  return required ? null : '';
}

function readStringArray(record: Record<string, unknown>, key: string): string[] {
  const value = record[key];
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0);
}

function parseProactiveCaps(raw: unknown): CompanionProactiveCaps {
  if (!isRecord(raw)) {
    return { ...DEFAULT_COMPANION_PROACTIVE_CAPS };
  }

  return {
    enabled: raw.enabled !== false,
    maxNudgesPerPage:
      typeof raw.maxNudgesPerPage === 'number' && raw.maxNudgesPerPage >= 0
        ? raw.maxNudgesPerPage
        : DEFAULT_COMPANION_PROACTIVE_CAPS.maxNudgesPerPage,
    maxNudgesPerSession:
      typeof raw.maxNudgesPerSession === 'number' && raw.maxNudgesPerSession >= 0
        ? raw.maxNudgesPerSession
        : DEFAULT_COMPANION_PROACTIVE_CAPS.maxNudgesPerSession,
    dismissCoolOffDays:
      typeof raw.dismissCoolOffDays === 'number' && raw.dismissCoolOffDays >= 0
        ? raw.dismissCoolOffDays
        : DEFAULT_COMPANION_PROACTIVE_CAPS.dismissCoolOffDays
  };
}

function parseAsrConfig(raw: unknown): CompanionAsrConfig | undefined {
  if (!isRecord(raw)) {
    return undefined;
  }
  const engine = raw.engine === 'camlin' ? 'camlin' : 'azure';
  if (engine === 'azure') {
    return undefined; // the default — nothing to carry
  }
  return {
    engine,
    tokenUrl: readString(raw, 'tokenUrl', false) || undefined,
    recognizeUrl: readString(raw, 'recognizeUrl', false) || undefined,
    language: readString(raw, 'language', false) || undefined,
    streaming: raw.streaming === true || undefined,
    streamUrl: readString(raw, 'streamUrl', false) || undefined
  };
}

function parseProactiveRules(raw: unknown): CompanionProactiveRules {
  if (!isRecord(raw)) {
    return {
      triggers: [],
      caps: { ...DEFAULT_COMPANION_PROACTIVE_CAPS }
    };
  }

  const triggersRaw = Array.isArray(raw.triggers) ? raw.triggers : [];
  const triggers = triggersRaw
    .filter(isRecord)
    .map((trigger) => ({
      kind: trigger.kind as CompanionProactiveRules['triggers'][number]['kind'],
      match: readString(trigger, 'match') || '',
      dwellSeconds: typeof trigger.dwellSeconds === 'number' ? trigger.dwellSeconds : undefined,
      scrollFraction:
        typeof trigger.scrollFraction === 'number' ? trigger.scrollFraction : undefined,
      nudgeMessage: readString(trigger, 'nudgeMessage', false) || undefined
    }))
    .filter((trigger) => trigger.match.length > 0);

  return {
    triggers,
    caps: parseProactiveCaps(raw.caps)
  };
}

function parseTranscriptRetention(raw: unknown): CompanionTranscriptRetention {
  if (!isRecord(raw)) {
    return { ...DEFAULT_COMPANION_TRANSCRIPT_RETENTION };
  }

  return {
    enabled: raw.enabled !== false,
    retentionDays:
      typeof raw.retentionDays === 'number' && raw.retentionDays > 0
        ? raw.retentionDays
        : DEFAULT_COMPANION_TRANSCRIPT_RETENTION.retentionDays
  };
}

function parseManifestPack(raw: unknown): CompanionAnnotationManifestPack {
  if (!isRecord(raw)) {
    return { version: 1, sections: [], actions: [], fields: [] };
  }

  const sections = (Array.isArray(raw.sections) ? raw.sections : [])
    .filter(isRecord)
    .map((section) => ({
      id: readString(section, 'id') || '',
      label: readString(section, 'label') || '',
      description: readString(section, 'description', false) || undefined
    }))
    .filter((section) => section.id && section.label);

  const actions = (Array.isArray(raw.actions) ? raw.actions : [])
    .filter(isRecord)
    .map((action) => ({
      id: readString(action, 'id') || '',
      label: readString(action, 'label') || '',
      description: readString(action, 'description') || '',
      sectionId: readString(action, 'sectionId', false) || undefined,
      risk: (action.risk as CompanionAnnotationManifestPack['actions'][number]['risk']) || 'safe',
      paramsSchema: isRecord(action.paramsSchema) ? action.paramsSchema : undefined
    }))
    .filter((action) => action.id && action.label);

  const fields = (Array.isArray(raw.fields) ? raw.fields : [])
    .filter(isRecord)
    .map((field) => ({
      id: readString(field, 'id') || '',
      label: readString(field, 'label') || '',
      description: readString(field, 'description', false) || undefined,
      inputType: field.inputType as CompanionAnnotationManifestPack['fields'][number]['inputType']
    }))
    .filter((field) => field.id && field.label);

  return {
    version: typeof raw.version === 'number' ? raw.version : 1,
    sections,
    actions,
    fields
  };
}

export function parseCompanionSiteConfig(
  raw: unknown,
  expectedSiteKey?: CompanionSiteKey
): CompanionConfigParseResult {
  const errors: string[] = [];

  if (!isRecord(raw)) {
    return { ok: false, errors: ['config must be an object'] };
  }

  const siteKey = readString(raw, 'siteKey');
  if (!siteKey) {
    errors.push('siteKey is required');
  } else if (expectedSiteKey && siteKey !== expectedSiteKey) {
    errors.push('siteKey mismatch');
  }

  const tenantId = readString(raw, 'tenantId');
  if (!tenantId) {
    errors.push('tenantId is required');
  }

  const siteName = readString(raw, 'siteName');
  if (!siteName) {
    errors.push('siteName is required');
  }

  const personaRaw = raw.persona;
  if (!isRecord(personaRaw)) {
    errors.push('persona is required');
  }

  const displayName = personaRaw && isRecord(personaRaw) ? readString(personaRaw, 'displayName') : null;
  const specialistPrompt =
    personaRaw && isRecord(personaRaw) ? readString(personaRaw, 'specialistPrompt') : null;

  if (!displayName) {
    errors.push('persona.displayName is required');
  }
  if (!specialistPrompt) {
    errors.push('persona.specialistPrompt is required');
  }

  if (errors.length > 0) {
    return { ok: false, errors };
  }

  const avatarRaw = isRecord(raw.avatar) ? raw.avatar : {};
  const knowledgeSourcesRaw = Array.isArray(raw.knowledgeSources) ? raw.knowledgeSources : [];
  const knowledgeSources = knowledgeSourcesRaw
    .filter(isRecord)
    .map((source) => ({
      kind: source.kind as CompanionSiteConfig['knowledgeSources'][number]['kind'],
      value: readString(source, 'value') || '',
      label: readString(source, 'label', false) || undefined
    }))
    .filter((source) => source.value.length > 0);

  const actionRiskOverridesRaw = Array.isArray(raw.actionRiskOverrides)
    ? raw.actionRiskOverrides
    : [];
  const actionRiskOverrides = actionRiskOverridesRaw
    .filter(isRecord)
    .map((override) => ({
      actionId: readString(override, 'actionId') || '',
      risk: (override.risk as CompanionSiteConfig['actionRiskOverrides'][number]['risk']) || 'safe'
    }))
    .filter((override) => override.actionId.length > 0);

  const config: CompanionSiteConfig = {
    siteKey: siteKey as CompanionSiteKey,
    tenantId: tenantId as string,
    siteName: siteName as string,
    knowledgeSources,
    persona: {
      displayName: displayName as string,
      specialistPrompt: specialistPrompt as string
    },
    avatar: {
      modelId: readString(avatarRaw, 'modelId', false) || undefined,
      voiceId: readString(avatarRaw, 'voiceId', false) || undefined,
      position:
        (avatarRaw.position as CompanionSiteConfig['avatar']['position']) || 'bottom-right',
      theme: (avatarRaw.theme as CompanionSiteConfig['avatar']['theme']) || 'auto',
      // 2.12.0 — only explicit booleans survive; anything else means "default".
      lipSync: isRecord(avatarRaw.lipSync)
        ? {
            ...(typeof avatarRaw.lipSync.progressive === 'boolean'
              ? { progressive: avatarRaw.lipSync.progressive }
              : {}),
            ...(typeof avatarRaw.lipSync.greetingWaitsForModel === 'boolean'
              ? { greetingWaitsForModel: avatarRaw.lipSync.greetingWaitsForModel }
              : {})
          }
        : undefined
    },
    allowedOrigins: readStringArray(raw, 'allowedOrigins'),
    allowedActionIds: readStringArray(raw, 'allowedActionIds'),
    actionRiskOverrides,
    proactive: parseProactiveRules(raw.proactive),
    transcriptRetention: parseTranscriptRetention(raw.transcriptRetention),
    manifestPack: parseManifestPack(raw.manifestPack),
    runtimeUrl: readString(raw, 'runtimeUrl', false) || undefined,
    runtimeTenantId: readString(raw, 'runtimeTenantId', false) || undefined,
    voiceUrl: readString(raw, 'voiceUrl', false) || undefined,
    asr: parseAsrConfig(raw.asr),
    privacyUrl: readString(raw, 'privacyUrl', false) || undefined
  };

  return { ok: true, config, errors: [] };
}
